import { useSelector } from 'react-redux';
import { selectOffers } from '../reducers/offersReducer';
import { selectFilters } from '../reducers/filtersReducer';
import { parseTrimline } from '../utils/parseTrimline';
import { selectZipCode } from '../reducers/zipCodeReducer';

export const useAvailableFilterOptions = () => {
  const offers = useSelector(selectOffers).offersList;
  const { confirmedFilters } = useSelector(selectFilters);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  // Helper function to ensure selected options are always available
  const includeSelectedOptions = (availableOptions: string[], selectedOptions: string[]) => {
    return [...new Set([...availableOptions, ...selectedOptions])];
  };

  // Helper function that also includes counts
  const includeSelectedOptionsWithCounts = (
    availableOptions: Record<string, number>,
    selectedOptions: string[],
  ): Record<string, number> => {
    const result = { ...availableOptions };

    selectedOptions.forEach((modelYear) => {
      if (!result[modelYear]) {
        result[modelYear] = 0; // Add missing selected options with count 0
      }
    });

    return result;
  };

  // Filter offers based on currently selected filters
  const filteredOffers = offers.filter((offer) => {
    return (
      (!isCaMarket || confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
      (!isCaMarket ||
        !confirmedFilters.offerRegion.length ||
        confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
      (!confirmedFilters.offerType.length ||
        confirmedFilters.offerType.includes(offer?.offerType?.toLowerCase())) &&
      (!confirmedFilters.bodyType.length ||
        confirmedFilters.bodyType.includes(offer?.offerModelDetail?.bodyType?.toLowerCase())) &&
      (!confirmedFilters.modelFamily.length ||
        confirmedFilters.modelFamily.includes(
          offer?.offerModelDetail?.modelFamily?.replace(/[ -]/g, '').toLowerCase(),
        )) &&
      (!confirmedFilters.trimLine.length ||
        confirmedFilters.trimLine.includes(parseTrimline(offer?.offerModelDetail?.trimline))) &&
      (!confirmedFilters.modelYear.length ||
        confirmedFilters.modelYear.includes(
          offer?.offerModelDetail?.modelYear?.toString().toLowerCase(),
        ))
    );
  });

  // Generate available options for offerType and always include selected options
  const offerTypeOptions = includeSelectedOptions(
    [
      ...new Set(
        offers
          .filter(
            (offer) =>
              (!isCaMarket ||
                confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
              (!isCaMarket ||
                !confirmedFilters.offerRegion.length ||
                confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
              (!confirmedFilters.bodyType.length ||
                confirmedFilters.bodyType.includes(
                  offer.offerModelDetail.bodyType.toLowerCase(),
                )) &&
              (!confirmedFilters.modelFamily.length ||
                confirmedFilters.modelFamily.includes(
                  offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
                )) &&
              (!confirmedFilters.trimLine.length ||
                confirmedFilters.trimLine.includes(
                  parseTrimline(offer.offerModelDetail.trimline),
                )) &&
              (!confirmedFilters.modelYear.length ||
                confirmedFilters.modelYear.includes(offer.offerModelDetail.modelYear.toString())),
          )
          .map((offer) => offer.offerType.toLowerCase()),
      ),
    ],
    confirmedFilters.offerType, // Always include selected offerType options
  );

  const bodyTypeOptions = includeSelectedOptions(
    [
      ...new Set(
        offers
          .filter(
            (offer) =>
              (!isCaMarket ||
                confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
              (!isCaMarket ||
                !confirmedFilters.offerRegion.length ||
                confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
              (!confirmedFilters.offerType.length ||
                confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
              (!confirmedFilters.modelFamily.length ||
                confirmedFilters.modelFamily.includes(
                  offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
                )) &&
              (!confirmedFilters.trimLine.length ||
                confirmedFilters.trimLine.includes(
                  parseTrimline(offer.offerModelDetail.trimline),
                )) &&
              (!confirmedFilters.modelYear.length ||
                confirmedFilters.modelYear.includes(offer.offerModelDetail.modelYear.toString())),
          )
          .map((offer) => offer.offerModelDetail.bodyType.toLowerCase()),
      ),
    ],
    confirmedFilters.bodyType, // Always include selected bodyType options
  );

  const modelFamilyOptions = includeSelectedOptions(
    [
      ...new Set(
        offers
          .filter(
            (offer) =>
              (!isCaMarket ||
                confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
              (!isCaMarket ||
                !confirmedFilters.offerRegion.length ||
                confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
              (!confirmedFilters.offerType.length ||
                confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
              (!confirmedFilters.bodyType.length ||
                confirmedFilters.bodyType.includes(
                  offer.offerModelDetail.bodyType.toLowerCase(),
                )) &&
              (!confirmedFilters.trimLine.length ||
                confirmedFilters.trimLine.includes(
                  parseTrimline(offer.offerModelDetail.trimline),
                )) &&
              (!confirmedFilters.modelYear.length ||
                confirmedFilters.modelYear.includes(offer.offerModelDetail.modelYear.toString())),
          )
          .map((offer) => offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase()),
      ),
    ],
    confirmedFilters.modelFamily, // Always include selected modelFamily options
  );

  const trimLineOptions = includeSelectedOptions(
    [
      ...new Set(
        offers
          .filter(
            (offer) =>
              (!isCaMarket ||
                confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
              (!isCaMarket ||
                !confirmedFilters.offerRegion.length ||
                confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
              (!confirmedFilters.offerType.length ||
                confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
              (!confirmedFilters.bodyType.length ||
                confirmedFilters.bodyType.includes(
                  offer.offerModelDetail.bodyType.toLowerCase(),
                )) &&
              (!confirmedFilters.modelFamily.length ||
                confirmedFilters.modelFamily.includes(
                  offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
                )) &&
              (!confirmedFilters.modelYear.length ||
                confirmedFilters.modelYear.includes(offer.offerModelDetail.modelYear.toString())),
          )
          .map((offer) => parseTrimline(offer.offerModelDetail.trimline)),
      ),
    ],
    confirmedFilters.trimLine, // Always include selected trimLine options
  );

  const offerRegionOptions = includeSelectedOptions(
    [
      ...new Set(
        offers
          .filter(
            (offer) =>
              (!isCaMarket ||
                confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
              (!confirmedFilters.offerType.length ||
                confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
              (!confirmedFilters.bodyType.length ||
                confirmedFilters.bodyType.includes(
                  offer.offerModelDetail.bodyType.toLowerCase(),
                )) &&
              (!confirmedFilters.modelFamily.length ||
                confirmedFilters.modelFamily.includes(
                  offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
                )) &&
              (!confirmedFilters.trimLine.length ||
                confirmedFilters.trimLine.includes(
                  parseTrimline(offer.offerModelDetail.trimline),
                )) &&
              (!confirmedFilters.modelYear.length ||
                confirmedFilters.modelYear.includes(offer.offerModelDetail.modelYear.toString())),
          )
          .map((offer) => offer.offerModelDetail.dag),
      ),
    ],
    confirmedFilters.offerRegion, // Always include selected modelFamily options
  );

  // Generate offerType options with counts (excludes offerType from filter, same as offerTypeOptions)
  const offerTypeOptionsWithCounts = includeSelectedOptionsWithCounts(
    offers
      .filter(
        (offer) =>
          (!isCaMarket || confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
          (!isCaMarket ||
            !confirmedFilters.offerRegion.length ||
            confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
          (!confirmedFilters.bodyType.length ||
            confirmedFilters.bodyType.includes(offer.offerModelDetail.bodyType.toLowerCase())) &&
          (!confirmedFilters.modelFamily.length ||
            confirmedFilters.modelFamily.includes(
              offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
            )) &&
          (!confirmedFilters.trimLine.length ||
            confirmedFilters.trimLine.includes(parseTrimline(offer.offerModelDetail.trimline))) &&
          (!confirmedFilters.modelYear.length ||
            confirmedFilters.modelYear.includes(offer.offerModelDetail.modelYear.toString())),
      )
      .reduce(
        (acc, offer) => {
          const offerType = offer.offerType.toLowerCase();
          acc[offerType] = (acc[offerType] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
    confirmedFilters.offerType,
  );

  // Generate modelYear options with counts
  const modelYearOptionsWithCounts = includeSelectedOptionsWithCounts(
    offers
      .filter(
        (offer) =>
          (!isCaMarket || confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
          (!isCaMarket ||
            !confirmedFilters.offerRegion.length ||
            confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
          (!confirmedFilters.offerType.length ||
            confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
          (!confirmedFilters.bodyType.length ||
            confirmedFilters.bodyType.includes(offer.offerModelDetail.bodyType.toLowerCase())) &&
          (!confirmedFilters.modelFamily.length ||
            confirmedFilters.modelFamily.includes(
              offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
            )) &&
          (!confirmedFilters.trimLine.length ||
            confirmedFilters.trimLine.includes(parseTrimline(offer.offerModelDetail.trimline))),
      )
      .reduce(
        (acc, offer) => {
          const modelYear = offer.offerModelDetail.modelYear.toString().toLowerCase();
          acc[modelYear] = (acc[modelYear] || 0) + 1; // Count occurrences
          return acc;
        },
        {} as Record<string, number>,
      ),
    confirmedFilters.modelYear, // Always include selected modelYear options
  );

  return {
    availableOfferTypeOptions: offerTypeOptions,
    availableOfferTypeOptionsWithCounts: offerTypeOptionsWithCounts,
    availableBodyTypeOptions: bodyTypeOptions,
    availableModelFamilyOptions: modelFamilyOptions,
    availableTrimLineOptions: trimLineOptions,
    availableModelYearOptions: modelYearOptionsWithCounts,
    availableOfferRegionOptions: offerRegionOptions,
    totalAvailableOffers: filteredOffers.length,
  };
};
